import React, { useState, useEffect, useRef } from "react";
import { FaMicrophone, FaPaperPlane } from "react-icons/fa6";
import { LuUserSearch } from "react-icons/lu";
import { SiGoogleassistant } from "react-icons/si";
import { PiWaveformBold } from "react-icons/pi";
import logo from "./utils/logo2.png";
import Orb from "./Orb";
import AddQuestionModal from "./AddQuestionModal";
import LoginModal from "./LoginModal";
import { getToken } from "./auth";
const showAddButton = window.location.pathname === "/login";

const BACKEND_URL = "https://staging.parkengage.ai/api";

let sharedAudioCtx = null;

const ChatbotWidget = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [started, setStarted] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [listening, setListening] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const recognitionRef = useRef(null);
  const transcriptSeenRef = useRef(false);
  const transcriptTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const ignoreSttRef = useRef(false);

  // NEW: track whether we already used a direct user gesture to warm up audio/tts/stt
  const userGestureRef = useRef(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "chatbot-widget-ios-fixes";
    style.innerHTML = `
      .no-scroll-touch { touch-action: none !important; -webkit-user-select: none; -webkit-touch-callout: none; }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById("chatbot-widget-ios-fixes");
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const unlock = () => {
      if (!sharedAudioCtx) {
        sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
        console.log("🔓 Global AudioContext created");
      }
      if (sharedAudioCtx.state === "suspended") {
        sharedAudioCtx.resume();
        console.log("🎧 AudioContext resumed");
      }
    };

    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("click", unlock, { once: true });

    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      console.warn("⚠️ SpeechRecognition not supported in this browser.");
      recognitionRef.current = null;
      return;
    }

    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (event) => {
        if (ignoreSttRef.current) return; 
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const res = event.results[i];
        if (res.isFinal) final += res[0].transcript;
        else interim += res[0].transcript;
      }

      if (interim || final) {
        transcriptSeenRef.current = true;
        if (transcriptTimeoutRef.current) {
          clearTimeout(transcriptTimeoutRef.current);
          transcriptTimeoutRef.current = null;
        }
      }

      if (interim) setInput(interim);
      if (final) setInput(final.trim());
    };

    rec.onerror = (err) => {
      console.warn("SpeechRecognition error:", err);
      setListening(false);
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
        transcriptTimeoutRef.current = null;
      }
    };

    rec.onend = () => {
      setListening(false);
       ignoreSttRef.current = false;
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
        transcriptTimeoutRef.current = null;
      }
    };

    // ensure we update listening state when recognition actually starts
    rec.onstart = () => {
      setListening(true);
    };

    recognitionRef.current = rec;
    return () => {
      try {
        if (recognitionRef.current) {
          recognitionRef.current.onresult = null;
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onstart = null;
          recognitionRef.current = null;
        }
      } catch {}
    };
  }, []);

  const speak = async (text, langOverride = null) => {
    if (!text) return;
    if (!window.speechSynthesis) return;

    if (recognitionRef.current && listening) {
      try { recognitionRef.current.stop(); } catch {}
      setListening(false);
      console.log("🤫 STT paused during TTS...");
    }

    const langCode = langOverride || "en-US";

    const ua = navigator.userAgent;
    const isEdge = /Edg/i.test(ua);
    const isChrome = /Chrome/i.test(ua) && !isEdge && !/Safari/i.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isMac = navigator.platform.toLowerCase().includes("mac");

    const edgeVoiceMap = {
      "hi-IN": /microsoft swara online.*natural/i,
      "en-US": /microsoft aria online.*natural/i,
      "es-ES": /microsoft elvira online.*natural/i,
      "fr-FR": /microsoft denise online.*natural/i,
    };

    const chromeVoiceMap = {
      "hi-IN": /google.*hindi/i,
      "en-US": /google us english female|google uk english female/i,
      "es-ES": /google español/i,
      "fr-FR": /google français/i,
    };

    const safariVoiceMap = {
      "hi-IN": /lekha/i,
      "en-US": /samantha/i,
      "es-ES": /monica|paulina/i,
      "fr-FR": /amelie|thomas/i,
    };

    async function loadVoicesOnIOS() {
      return new Promise((resolve) => {
        let voices = speechSynthesis.getVoices();
        if (voices.length > 0) return resolve(voices);

        const warmup = new SpeechSynthesisUtterance(" ");
        warmup.volume = 0;
        speechSynthesis.speak(warmup);

        const interval = setInterval(() => {
          voices = speechSynthesis.getVoices();
          if (voices.length > 0) {
            clearInterval(interval);
            resolve(voices);
          }
        }, 100);
      });
    }

    const voices = isIOS ? await loadVoicesOnIOS() : speechSynthesis.getVoices();
    console.log("🔊 Available voices:", voices.map((v) => v.name));

    let selectedVoice = null;
    let voiceMap;

    if (isSafari && isMac) voiceMap = safariVoiceMap;
    else if (isIOS) voiceMap = safariVoiceMap;
    else if (isEdge) voiceMap = edgeVoiceMap;
    else if (isChrome) voiceMap = chromeVoiceMap;

    if (voiceMap && voiceMap[langCode]) {
      selectedVoice = voices.find((v) => voiceMap[langCode].test(v.name));
    }

    if (!selectedVoice) {
      selectedVoice =
        voices.find((v) => v.lang === langCode && /female/i.test(v.name)) ||
        voices.find((v) => /samantha|aria|zira|olivia|emma|hazel|victoria/i.test(v.name)) ||
        voices.find((v) => /en(-|_)us/i.test(v.lang)) ||
        voices[0];
    }

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = langCode;
    if (selectedVoice) utter.voice = selectedVoice;

    utter.pitch = /\?$/.test(text) ? 1.2 : 1.1;
    utter.rate = /\?$/.test(text) ? 1.05 : 1.0;

    utter.onend = () => {
      console.log("✅ Web TTS finished → STT stays OFF until user taps mic");
      setListening(false);
    };

    speechSynthesis.cancel();
    speechSynthesis.speak(utter);
  };

  // 👉 ADD THIS FUNCTION — minimal iOS warmup
  const warmupTTSiOS = () => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isIOS) return;

    try {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      speechSynthesis.speak(u);
      console.log("📢 iOS TTS permission warmed up");
    } catch (err) {
      console.log("iOS warmup failed", err);
    }
  };

  const handleSend = async (e, voiceText = null) => {
    if (e) e.preventDefault();

    // 🔥 iOS TTS PERMISSION FIX — RUN INSIDE USER TAP
    warmupTTSiOS();

    const question = (voiceText || input).trim();
    if (!question) return;

    setStarted(true);
    setMessages((prev) => [...prev, { text: question, sender: "user" }]);
    ignoreSttRef.current = true;
    setInput("");

    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = "24px";
    }

    try {
      const res = await fetch(`${BACKEND_URL}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          session_id: sessionId,
          meta: { garage: "default_garage" },
          lang: "en-US",
        }),
      });

      const data = await res.json();
      const answer = data.text || "Sorry, I couldn’t get that.";
      setMessages((prev) => [...prev, { text: answer, sender: "ai" }]);

      const backendLang = data.lang || "en-US";
      if (recognitionRef.current) recognitionRef.current.detectedLang = backendLang;
      speak(answer, backendLang);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { text: "⚠️ Server error. Please try again.", sender: "ai" },
      ]);
    }
  };

  const handleIconClick = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.stop();
      } catch {}
    }
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }
    setShowAnimation(true);
  };

// iOS requires recognition to be re-instantiated after every stop
const recreateRecognition = () => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;

  const rec = new SR();
  rec.continuous = false;
  rec.interimResults = true;
  rec.lang = "en-US";

  // IMPORTANT: Attach new handlers — DO NOT copy old ones
  rec.onresult = (event) => {
    if (ignoreSttRef.current) return;

    let interim = "", final = "";
    for (let i = event.resultIndex; i < event.results.length; ++i) {
      const res = event.results[i];
      if (res.isFinal) final += res[0].transcript;
      else interim += res[0].transcript;
    }

    if (interim || final) {
      transcriptSeenRef.current = true;
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
        transcriptTimeoutRef.current = null;
      }
    }

    if (interim) setInput(interim);
    if (final) setInput(final.trim());
  };

  rec.onerror = (err) => {
    console.warn("SpeechRecognition error:", err);
    setListening(false);
  };

  rec.onend = () => {
    setListening(false);
    ignoreSttRef.current = false;
    if (transcriptTimeoutRef.current) {
      clearTimeout(transcriptTimeoutRef.current);
      transcriptTimeoutRef.current = null;
    }
  };

  rec.onstart = () => setListening(true);

  recognitionRef.current = rec;
  console.log("🎤 iOS STT re-created with fresh event handlers");
  return rec;
};



const startRecognition = () => {
  let rec;
if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
  rec = recreateRecognition();   // now uses fresh handlers
} else {
  rec = recognitionRef.current;
}


  // 🔥 iOS: recreate recognition every time after first use
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    rec = recreateRecognition();
  }

  if (!rec) {
    console.warn("SpeechRecognition not available");
    return;
  }

  transcriptSeenRef.current = false;

  try {
    ignoreSttRef.current = false;

    rec.start();
    setListening(true);
    console.log("🎤 STT started");
  } catch (err) {
    console.error("startRecognition failed:", err);
    setListening(false);
  }
};


  const stopRecognition = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    try {
      rec.stop();
    } catch (err) {
      console.warn("stopRecognition error:", err);
    } finally {
      setListening(false);
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
        transcriptTimeoutRef.current = null;
      }
    }
  };

  // NEW: handle mic press within the user gesture. This MUST run synchronously in the click/touch handler.
  const handleMicButtonPress = (e) => {
    // optional: keep touch events from also causing a blur/other behavior
    if (e && e.preventDefault) e.preventDefault();

    // Warm up TTS (iOS) and resume/create AudioContext synchronously — keeps it inside the user gesture
    warmupTTSiOS();

    if (!sharedAudioCtx) sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    try {
      if (sharedAudioCtx.state === "suspended") sharedAudioCtx.resume();
    } catch (err) {
      console.warn("AudioContext resume failed in gesture:", err);
    }

    userGestureRef.current = true; // mark that we've used a real user gesture

    // Toggle recognition immediately while inside the gesture to satisfy iOS permission model
    if (listening) stopRecognition();
    else startRecognition();
  };

const toggleMic = () => {
  // ensures AudioContext is resumed on every tap
  if (sharedAudioCtx && sharedAudioCtx.state === "suspended") {
    sharedAudioCtx.resume();
  }

  if (listening) stopRecognition();
  else startRecognition();   // this is now iOS-safe
};


  return (
    <div className="flex flex-col min-h-screen bg-white relative px-4 sm:px-6 md:px-10 py-6 sm:py-8">
      <div
        className="
          absolute top-4 sm:top-5 md:top-6 
          left-1/2 transform -translate-x-1/2
          lg:left-10 lg:top-6 lg:transform-none
        "
      >
        <img
          src={logo}
          alt="ParkEngage Logo"
          className="h-10 sm:h-12 md:h-14 w-auto object-contain transition-all duration-300"
        />
      </div>

      {showAddButton && (
        <button
          onClick={() => {
            if (getToken()) {
              setAddModalOpen(true);
            } else {
              setLoginOpen(true);
            }
          }}
          className="absolute top-4 right-4 sm:top-5 md:top-6
             text-blue-600 text-sm sm:text-base font-semibold
             underline hover:text-blue-800 transition cursor-pointer bg-transparent shadow-none px-0 py-0"
          title="Add Question"
        >
          Add Q&A
        </button>
      )}

      {addModalOpen && <AddQuestionModal onClose={() => setAddModalOpen(false)} />}
      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} onLoginSuccess={() => setAddModalOpen(true)} />}

      <div className="flex flex-col items-center justify-center flex-1 mt-10 lg:mt-6">
        {showAnimation ? (
          <Orb onClose={() => setShowAnimation(false)} />
        ) : (
          <>
            {!started && (
              <h1
                className="text-xl sm:text-2xl md:text-3xl lg:text-2xl font-semibold text-center px-4 mb-2 sm:mb-3"
                style={{ color: "#305688ff" }}
              >
                Experience true digital parking with{" "}
                <span className="font-bold text-blue-400">ParkEngage</span>
              </h1>
            )}

            <div
              className={`flex flex-col w-full max-w-md sm:max-w-2xl md:max-w-3xl 
                ${started ? "h-[70vh] sm:h-[76vh]" : "h-auto"} 
                mt-2
                border border-gray-200 rounded-2xl shadow-lg bg-white overflow-hidden 
                transition-all duration-500`}
            >
              {started && (
                <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-4 bg-gray-50">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex items-start ${msg.sender === "user" ? "justify-end" : "justify-start"
                        }`}
                    >
                      <div
                        className={`${msg.sender === "user" ? "order-2 ml-2" : "mr-2"
                          } flex items-center`}
                      >
                        {msg.sender === "user" ? (
                          <LuUserSearch className="text-[#174A8C] text-xl sm:text-2xl md:text-3xl lg:text-base" />
                        ) : (
                          <SiGoogleassistant className="text-gray-800 text-xl sm:text-2xl md:text-3xl lg:text-base" />
                        )}
                      </div>

                      <div
                        className={`px-4 py-2 rounded-xl max-w-[80%] text-base sm:text-lg md:text-xl lg:text-sm break-words whitespace-pre-wrap ${msg.sender === "user"
                          ? "bg-[#e3f2fd] text-[#174A8C]"
                          : "bg-white text-gray-800 border border-gray-200"
                          }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}

              <form
                onSubmit={handleSend}
                className="flex items-center w-full px-3 py-2 sm:px-5 sm:py-3 border-t border-gray-200 bg-white"
              >
                <div className="flex items-end w-full bg-gray-100 rounded-md px-3 py-1.5 sm:px-4 sm:py-2 shadow-md">
                  <textarea
                    ref={inputRef}
                    className="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-500 resize-none overflow-hidden transition-[height] duration-200 text-base sm:text-lg md:text-xl lg:text-sm"
                    placeholder="Ask anything..."
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    rows={1}
                    style={{ maxHeight: "120px" }}
                  />

                  {input.trim() ? (
                    <button
                      type="submit"
                      className="text-[#174A8C] text-xl sm:text-2xl md:text-3xl lg:text-base mx-2 hover:scale-110 transition-transform"
                      title="Send"
                    >
                      <FaPaperPlane />
                    </button>
                  ) : (
                    <>
                      <div className="no-scroll-touch">
                        <button
                          type="button"
                          className={`text-[#174A8C] text-xl sm:text-2xl md:text-3xl lg:text-base mx-2 transition-transform ${listening
                            ? "text-red-600 animate-pulse scale-110"
                            : "hover:scale-110"
                            }`}
                          title="Tap to Talk (toggle)"
                          onClick={(e) => {
                            // Use the gesture-aware handler for iOS
                            handleMicButtonPress(e);
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <FaMicrophone />
                        </button>
                      </div>

                      <button
                        type="button"
                        className="text-[#174A8C] text-xl sm:text-2xl md:text-3xl lg:text-base hover:scale-110 transition-transform"
                        title="Tap and start Conversation"
                        onClick={handleIconClick}
                      >
                        <PiWaveformBold />
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatbotWidget;
